'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AlertTriangle, ChevronLeft, ChevronRight, User, Users, X } from 'lucide-react';
import { Drawer } from '@/shared/components/ui';
import { getEngagementCases, getFunnelCases } from '@/features/negociacoes/store/actions';
import type { DashboardCasesPage, NegociacaoCasoStatus } from '@/features/negociacoes/types';
import {
  statusLabelMap,
  statusPillClassMap,
} from '@/features/negociacoes/utils/negociacoesMappers';
import { useAppDispatch } from '@/store/hooks';
import type { PainelCasosSource } from '../types/analytics.types';

const PAGE_SIZE = 6;

interface PainelLateralCasosProps {
  source: PainelCasosSource | null;
  onClose: () => void;
}

const statusPill = (status: string) => {
  const statusValue = status as NegociacaoCasoStatus;
  const label = statusLabelMap[statusValue] ?? status;
  const pillClass = statusPillClassMap[statusValue] ?? '';
  return <span className={`neg-status-pill ${pillClass}`}>{label}</span>;
};

export const PainelLateralCasos: React.FC<PainelLateralCasosProps> = ({ source, onClose }) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<DashboardCasesPage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    (requestedPage: number) => {
      if (!source) return;
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      setHasError(false);
      const request =
        source.kind === 'funnel'
          ? dispatch(
              getFunnelCases({ stage: source.stage, page: requestedPage, limit: PAGE_SIZE }),
            ).unwrap()
          : dispatch(
              getEngagementCases({
                categoria: source.categoria,
                page: requestedPage,
                limit: PAGE_SIZE,
              }),
            ).unwrap();
      request
        .then((result) => {
          if (requestId !== requestIdRef.current) return;
          setData(result);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setHasError(true);
          setData({ items: [], page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 });
        })
        .finally(() => {
          if (requestId !== requestIdRef.current) return;
          setIsLoading(false);
        });
    },
    [dispatch, source],
  );

  useEffect(() => {
    requestIdRef.current++;
    setPage(1);
    setData(null);
    setHasError(false);
    if (source) fetchPage(1);
  }, [source, fetchPage]);

  const changePage = (nextPage: number) => {
    setPage(nextPage);
    fetchPage(nextPage);
  };

  if (!source) return null;

  const totalPages = data?.totalPages ?? 0;
  const canPrev = page > 1 && !isLoading;
  const canNext = totalPages > 0 && page < totalPages && !isLoading;
  const isEmpty = !isLoading && !hasError && data !== null && data.total === 0;

  return (
    <Drawer open onOpenChange={(open) => !open && onClose()} className="bg-transparent p-0">
      <Drawer.Content className="animate-slide-in-right h-full w-full bg-white shadow-xl sm:w-[420px]">
        <div className="flex h-full w-full flex-col">
          <div className="flex shrink-0 items-start justify-between border-b border-neutral-100 px-5 pt-5 pb-4">
            <div>
              <div className="mb-1 flex items-center gap-2">
                <div
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: source.color }}
                />
                <span className="text-body-bold font-body-bold text-neutral-900">
                  {source.title}
                </span>
              </div>
              <span className="text-caption font-caption text-subtext-color">
                {hasError ? 'Erro ao carregar' : data ? `${data.total} caso(s)` : 'Carregando...'}
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="ml-3 shrink-0 rounded-md p-1 text-neutral-500 hover:bg-neutral-100"
              aria-label="Fechar painel"
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col gap-3 p-5">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="h-12 animate-pulse rounded-lg bg-neutral-100" />
                ))}
              </div>
            )}

            {hasError && !isLoading && (
              <div className="flex flex-col items-center justify-center gap-2.5 px-6 py-10 text-center">
                <div className="bg-error-50 mb-1 flex h-12 w-12 items-center justify-center rounded-xl">
                  <AlertTriangle className="text-error-500 h-[22px] w-[22px]" />
                </div>
                <div className="text-body-bold font-body-bold text-neutral-700">
                  Não foi possível carregar os casos
                </div>
                <div className="text-caption font-caption text-subtext-color max-w-[260px]">
                  Ocorreu um erro ao buscar os casos. Tente novamente.
                </div>
                <button
                  type="button"
                  onClick={() => fetchPage(page)}
                  className="text-caption font-caption mt-1 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-neutral-700 hover:bg-neutral-50"
                >
                  Tentar novamente
                </button>
              </div>
            )}

            {isEmpty && (
              <div className="flex flex-col items-center justify-center gap-2.5 px-6 py-10 text-center">
                <div className="mb-1 flex h-12 w-12 items-center justify-center rounded-xl bg-neutral-100">
                  <Users className="h-[22px] w-[22px] text-neutral-400" />
                </div>
                <div className="text-body-bold font-body-bold text-neutral-700">
                  Nenhum caso nesta etapa
                </div>
                <div className="text-caption font-caption text-subtext-color max-w-[260px]">
                  Não há casos registrados para esta categoria no período selecionado.
                </div>
              </div>
            )}

            {!isLoading &&
              !hasError &&
              data?.items.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => router.push(`/negociacoes/${item.id}`)}
                  className="flex w-full cursor-pointer items-center justify-between gap-3 border-b border-neutral-50 px-5 py-3 text-left hover:bg-neutral-50"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--kpi-icon-primary-bg)]">
                      <User
                        className="h-3.5 w-3.5"
                        style={{ color: 'var(--kpi-icon-primary-fg)' }}
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="text-caption-bold font-caption-bold truncate text-neutral-900">
                        {item.counterpartyName}
                      </div>
                      <div className="text-caption font-caption text-subtext-color">
                        {item.numeroNegociacao ?? '—'}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {statusPill(item.status)}
                    <ChevronRight className="h-3.5 w-3.5 text-neutral-300" />
                  </div>
                </button>
              ))}
          </div>

          <div className="flex shrink-0 items-center justify-between border-t border-neutral-100 px-5 py-2.5">
            <button
              type="button"
              disabled={!canPrev}
              onClick={() => changePage(page - 1)}
              className="text-caption font-caption flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-neutral-700 disabled:cursor-default disabled:opacity-45"
            >
              <ChevronLeft className="h-3 w-3" />
              Anterior
            </button>
            <span className="text-caption font-caption text-subtext-color">
              {totalPages > 0 ? `Pág. ${page} de ${totalPages}` : '—'}
            </span>
            <button
              type="button"
              disabled={!canNext}
              onClick={() => changePage(page + 1)}
              className="text-caption font-caption flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-neutral-700 disabled:cursor-default disabled:opacity-45"
            >
              Próximo
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </Drawer.Content>
    </Drawer>
  );
};
